import { create } from 'zustand'
import { appLockService, BiometryType } from '@/lib/app-lock'

interface AppLockState {
  // State
  isLocked: boolean
  isSetupComplete: boolean // true if user has PIN set (server-side)
  biometricEnabled: boolean // device-specific
  biometricAvailable: boolean
  biometryType: BiometryType
  lockTimeout: number // device-specific
  failedAttempts: number
  isInitialized: boolean

  // Actions
  initialize: () => Promise<void>
  lock: () => void
  unlock: () => void
  checkAndLock: () => Promise<void>
  setLastActive: () => Promise<void>
  incrementFailedAttempts: () => void
  resetFailedAttempts: () => void

  // Settings actions
  enableAppLock: (pin: string) => Promise<void>
  changePIN: (newPin: string) => Promise<void>
  setBiometricEnabled: (enabled: boolean) => Promise<void>
  setLockTimeout: (minutes: number) => Promise<void>
  reset: () => void // Reset store on logout
}

export const useAppLockStore = create<AppLockState>((set, get) => ({
  isLocked: false,
  isSetupComplete: false,
  biometricEnabled: false,
  biometricAvailable: false,
  biometryType: BiometryType.NONE,
  lockTimeout: 2,
  failedAttempts: 0,
  isInitialized: false,

  initialize: async () => {
    // Don't re-initialize
    if (get().isInitialized) return

    try {
      const settings = await appLockService.getSettings()
      const hasPIN = await appLockService.hasPIN()
      const biometric = await appLockService.isBiometricAvailable()

      set({
        biometricEnabled: settings.biometricEnabled,
        lockTimeout: settings.lockTimeout,
        isSetupComplete: hasPIN,
        biometricAvailable: biometric.available,
        biometryType: biometric.type,
        isInitialized: true,
      })
    } catch {
      set({ isInitialized: true })
    }
  },

  lock: () => {
    set({ isLocked: true })
  },

  unlock: () => {
    set({ isLocked: false, failedAttempts: 0 })
    appLockService.setLastActiveTime()
  },

  checkAndLock: async () => {
    const shouldLock = await appLockService.shouldLock()
    if (shouldLock) {
      set({ isLocked: true })
    }
  },

  setLastActive: async () => {
    await appLockService.setLastActiveTime()
  },

  incrementFailedAttempts: () => {
    set((state) => ({ failedAttempts: state.failedAttempts + 1 }))
  },

  resetFailedAttempts: () => {
    set({ failedAttempts: 0 })
  },

  enableAppLock: async (pin) => {
    await appLockService.setPIN(pin)
    await appLockService.setLastActiveTime()
    set({ isSetupComplete: true })
  },

  changePIN: async (newPin) => {
    await appLockService.setPIN(newPin)
  },

  setBiometricEnabled: async (enabled) => {
    await appLockService.saveSettings({ biometricEnabled: enabled })
    set({ biometricEnabled: enabled })
  },

  setLockTimeout: async (minutes) => {
    await appLockService.saveSettings({ lockTimeout: minutes })
    set({ lockTimeout: minutes })
  },

  reset: () => {
    set({
      isLocked: false,
      isSetupComplete: false,
      biometricEnabled: false,
      biometricAvailable: false,
      biometryType: BiometryType.NONE,
      lockTimeout: 2,
      failedAttempts: 0,
      isInitialized: false,
    })
  },
}))
